import { useQuery, keepPreviousData } from '@tanstack/react-query';
import axios from 'axios';
import type { Transfer, Player } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL as string;

async function getTransfers(teamId: number, gameweek: string) {
  const { data } = await axios.get(
    `${API_BASE_URL}/api/team/${teamId}/transfers/${gameweek}`
  );
  return data as { transfers: Transfer[]; transfersCost: number };
}

async function getPlayers() {
  const { data } = await axios.get(`${API_BASE_URL}/api/players`);
  const map: Record<number, string> = {};
  (data as Player[]).forEach((p) => {
    map[p.id] = `${p.first_name} ${p.second_name}`;
  });
  return map;
}

export function useFetchTransfers(gameweek: string, teamId: number) {
  const transfersQuery = useQuery({
    queryKey: ['transfers', teamId, gameweek],
    queryFn: () => getTransfers(teamId, gameweek),
    staleTime: Infinity, // Will change this to gameweek deadline 
    gcTime: 1000 * 60 * 60 * 24 * 7, // keep a week in cache
    placeholderData: keepPreviousData,
  });

  const playersQuery = useQuery({
    queryKey: ['players'],
    queryFn: getPlayers,
    staleTime: 1000 * 60 * 60 * 24, // names are stable
  });

  const loading = transfersQuery.isLoading || playersQuery.isLoading;
  const error =
    (transfersQuery.error as Error | undefined)?.message ||
    (playersQuery.error as Error | undefined)?.message ||
    null;

  const transfers = transfersQuery.data?.transfers ?? [];
  const currentGameweekTransferCost =
    transfersQuery.data?.transfersCost ?? 0;

  return {
    transfers,
    loading,
    error,
    playerData: playersQuery.data ?? {},
    currentGameweekTransferCost,
  };
}
